import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Per-day time accounting for a single child. Stored in seconds for precision. */
interface ArcadeTimeEntry {
  date: string; // YYYY-MM-DD
  secondsUsed: number;
  bonusMinutesEarned: number;
}

interface ArcadeBonusEvent {
  earnedAt: string;
  activityId: string;
  activityTitle: string;
  minutesAwarded: number;
  stars: 1 | 2 | 3;
}

export type BoosterType = 'hammer' | 'bomb' | 'arrow' | 'shuffle';

export type BoosterInventory = Record<BoosterType, number>;

const DEFAULT_BOOSTERS: BoosterInventory = { hammer: 2, bomb: 1, arrow: 1, shuffle: 1 };
export const DEFAULT_ARCADE_BASE_MINUTES = 30;
export const DEFAULT_ARCADE_BONUS_CAP_MINUTES = 30;

export function normalizeArcadeMinutes(minutes: number, fallback = DEFAULT_ARCADE_BASE_MINUTES): number {
  const value = Number.isFinite(minutes) ? minutes : fallback;
  return Math.max(0, Math.min(120, Math.round(value)));
}

export interface ArcadeState {
  timeEntries: Record<string, ArcadeTimeEntry>; // key: `${childId}:${date}`
  bonusHistory: Record<string, ArcadeBonusEvent[]>; // key: childId — capped to 50
  boosters: Record<string, BoosterInventory>; // key: childId
  baseDailyMinutes: number;
  maxBonusMinutesPerDay: number;

  // Time helpers
  getOrCreateEntry: (childId: string) => ArcadeTimeEntry;
  getAllowedSecondsToday: (childId: string) => number;
  getRemainingSecondsToday: (childId: string) => number;
  getRemainingMinutesToday: (childId: string) => number;
  hasTimeRemaining: (childId: string) => boolean;

  // Actions
  addSecondsUsed: (childId: string, seconds: number) => void;
  awardBonusTime: (
    childId: string,
    activityId: string,
    activityTitle: string,
    minutes: number,
    stars: 1 | 2 | 3
  ) => void;
  addParentBonus: (childId: string, minutes: number) => void;
  setBaseDailyMinutes: (minutes: number) => void;
  setMaxBonusMinutesPerDay: (minutes: number) => void;

  // Boosters
  getBoosters: (childId: string) => BoosterInventory;
  awardBooster: (childId: string, type: BoosterType, amount?: number) => void;
  consumeBooster: (childId: string, type: BoosterType) => void;
}

function todayDate(): string {
  return new Date().toISOString().split('T')[0];
}

export const useArcadeStore = create<ArcadeState>()(
  persist(
    (set, get) => ({
      timeEntries: {},
      bonusHistory: {},
      boosters: {},
      baseDailyMinutes: DEFAULT_ARCADE_BASE_MINUTES,
      maxBonusMinutesPerDay: DEFAULT_ARCADE_BONUS_CAP_MINUTES,

      getOrCreateEntry: (childId: string): ArcadeTimeEntry => {
        const today = todayDate();
        const key = `${childId}:${today}`;
        const existing = get().timeEntries[key];
        if (existing && existing.date === today) return existing;
        const fresh: ArcadeTimeEntry = { date: today, secondsUsed: 0, bonusMinutesEarned: 0 };
        set(state => ({ timeEntries: { ...state.timeEntries, [key]: fresh } }));
        return fresh;
      },

      getAllowedSecondsToday: (childId: string): number => {
        const entry = get().getOrCreateEntry(childId);
        return (get().baseDailyMinutes + entry.bonusMinutesEarned) * 60;
      },

      getRemainingSecondsToday: (childId: string): number => {
        const entry = get().getOrCreateEntry(childId);
        const allowed = get().getAllowedSecondsToday(childId);
        return Math.max(0, allowed - entry.secondsUsed);
      },

      getRemainingMinutesToday: (childId: string): number => {
        return Math.ceil(get().getRemainingSecondsToday(childId) / 60);
      },

      hasTimeRemaining: (childId: string): boolean => {
        return get().getRemainingSecondsToday(childId) > 0;
      },

      addSecondsUsed: (childId: string, seconds: number) => {
        if (seconds <= 0) return;
        const today = todayDate();
        const key = `${childId}:${today}`;
        get().getOrCreateEntry(childId);
        set(state => {
          const entry = state.timeEntries[key];
          if (!entry) return state;
          return {
            timeEntries: {
              ...state.timeEntries,
              [key]: { ...entry, secondsUsed: entry.secondsUsed + seconds },
            },
          };
        });
      },

      awardBonusTime: (childId, activityId, activityTitle, minutes, stars) => {
        if (minutes <= 0) return;
        const today = todayDate();
        const key = `${childId}:${today}`;
        get().getOrCreateEntry(childId);

        // Enforce per-day bonus cap
        const cap = get().maxBonusMinutesPerDay;
        const currentBonus = get().timeEntries[key]?.bonusMinutesEarned ?? 0;
        const headroom = Math.max(0, cap - currentBonus);
        const actualMinutes = Math.min(minutes, headroom);
        if (actualMinutes <= 0) return;

        const bonusEvent: ArcadeBonusEvent = {
          earnedAt: new Date().toISOString(),
          activityId,
          activityTitle,
          minutesAwarded: actualMinutes,
          stars,
        };

        set(state => {
          const entry = state.timeEntries[key];
          const prevHistory = state.bonusHistory[childId] || [];
          return {
            timeEntries: entry
              ? {
                  ...state.timeEntries,
                  [key]: { ...entry, bonusMinutesEarned: entry.bonusMinutesEarned + actualMinutes },
                }
              : state.timeEntries,
            bonusHistory: {
              ...state.bonusHistory,
              [childId]: [...prevHistory, bonusEvent].slice(-50),
            },
          };
        });
      },

      addParentBonus: (childId: string, minutes: number) => {
        if (minutes <= 0) return;
        const today = todayDate();
        const key = `${childId}:${today}`;
        get().getOrCreateEntry(childId);
        set(state => {
          const entry = state.timeEntries[key];
          if (!entry) return state;
          return {
            timeEntries: {
              ...state.timeEntries,
              [key]: { ...entry, bonusMinutesEarned: entry.bonusMinutesEarned + minutes },
            },
          };
        });
      },

      setBaseDailyMinutes: (minutes: number) => set({ baseDailyMinutes: normalizeArcadeMinutes(minutes) }),
      setMaxBonusMinutesPerDay: (minutes: number) =>
        set({ maxBonusMinutesPerDay: normalizeArcadeMinutes(minutes, DEFAULT_ARCADE_BONUS_CAP_MINUTES) }),

      getBoosters: (childId: string): BoosterInventory => {
        const existing = get().boosters[childId];
        if (existing) return existing;
        // Seed defaults so first read returns stable inventory
        set(state => ({ boosters: { ...state.boosters, [childId]: { ...DEFAULT_BOOSTERS } } }));
        return { ...DEFAULT_BOOSTERS };
      },

      awardBooster: (childId: string, type: BoosterType, amount: number = 1) => {
        set(state => {
          const current = state.boosters[childId] || { ...DEFAULT_BOOSTERS };
          // Cap individual booster count at 9 to keep UI tidy
          const next = Math.min(9, current[type] + amount);
          return {
            boosters: {
              ...state.boosters,
              [childId]: { ...current, [type]: next },
            },
          };
        });
      },

      consumeBooster: (childId: string, type: BoosterType) => {
        set(state => {
          const current = state.boosters[childId] || { ...DEFAULT_BOOSTERS };
          if (current[type] <= 0) return state;
          return {
            boosters: {
              ...state.boosters,
              [childId]: { ...current, [type]: current[type] - 1 },
            },
          };
        });
      },
    }),
    {
      name: 'homeschool-arcade',
      version: 1,
      migrate: (persisted: unknown, fromVersion: number) => {
        const state = (persisted ?? {}) as Partial<ArcadeState>;
        if (fromVersion < 1) {
          state.baseDailyMinutes = state.baseDailyMinutes === 10
            ? DEFAULT_ARCADE_BASE_MINUTES
            : normalizeArcadeMinutes(state.baseDailyMinutes ?? DEFAULT_ARCADE_BASE_MINUTES);
          state.maxBonusMinutesPerDay = normalizeArcadeMinutes(
            state.maxBonusMinutesPerDay ?? DEFAULT_ARCADE_BONUS_CAP_MINUTES,
            DEFAULT_ARCADE_BONUS_CAP_MINUTES,
          );
        }
        return state as unknown as ArcadeState;
      },
    }
  )
);
